import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  Alert,
  Grid,
} from '@mui/material';
import { ProspectInterface } from 'api/canprospect/prospect';
import updateProspect from 'api/canprospect/updateProspect';

interface ProspectEditFormProps {
  open: boolean;
  onClose: () => void;
  prospect: ProspectInterface;
  onSaved: () => void;
}

const ProspectEditForm: React.FC<ProspectEditFormProps> = ({
  open,
  onClose,
  prospect,
  onSaved,
}) => {
  const [formData, setFormData] = useState<ProspectInterface>({ ...prospect });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleChange = (field: keyof ProspectInterface) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      await updateProspect(formData);
      onSaved();
      onClose();
    } catch (err) {
      console.error('Error updating prospect:', err);
      setError('Failed to update prospect. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const fields: { label: string; field: keyof ProspectInterface; multiline?: boolean; rows?: number }[] = [
    { label: 'Company Name', field: 'CompanyName' },
    { label: 'First Name', field: 'FirstName' },
    { label: 'Last Name', field: 'LastName' },
    { label: 'Title', field: 'TitleDescription' },
    { label: 'Phone', field: 'Phone' },
    { label: 'Email', field: 'Email' },
    { label: 'Address', field: 'Address' },
    { label: 'City', field: 'City' },
    { label: 'Province', field: 'Province' },
    { label: 'Postal Code', field: 'PostalCode' },
    { label: 'Website', field: 'Website' },
    { label: 'Comment', field: 'ProspectComment', multiline: true, rows: 3 },
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="edit-prospect-dialog-title"
    >
      <DialogTitle id="edit-prospect-dialog-title" sx={{ fontWeight: 700 }}>
        Edit Prospect
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2, mt: 1 }}>
            {error}
          </Alert>
        )}
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          {fields.map(({ label, field, multiline, rows }) => (
            <Grid item xs={12} sm={field === 'ProspectComment' ? 12 : 6} key={field}>
              <TextField
                label={label}
                value={formData[field] ?? ''}
                onChange={handleChange(field)}
                fullWidth
                size="small"
                multiline={multiline}
                rows={rows}
                variant="outlined"
              />
            </Grid>
          ))}
        </Grid>
      </DialogContent>
      {saving && <LinearProgress />}
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving}
          sx={{ fontWeight: 600, textTransform: 'none' }}
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProspectEditForm;
